import { FC, useEffect, useRef, useState } from 'react';
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import { OnPaymentCompletedData } from '@adyen/adyen-web/dist/types/components/types';
import { useDispatch } from 'react-redux';
import API_URLS from '../../../../../constants/api-urls';
import qualityEvents from '../../../../../services/quality-events/quality-events';
import { useAppSelector } from '../../../../../store';
import axios from '../../../../../utils/axios';
import emailHash from '../../../../../utils/email-hash';
import isMobileAppWithDeeplinkRedirect from '../../../../../utils/is-mobile-app';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';
import { AdyeConfig } from '../../../../../api/payment/get-payment-options';
import { PAYMENT_METHOD_KEYS } from '../../../payment.constants';
import { createAdyenCheckout, getAdyenSessionData } from '../../../payment.helpers';
import { addFailedPaymentMethod, addPaymentError, changeState, serializeForm, validateForm } from '../../../reducers';
import { getMobilePathParams } from '../../kushki/utils/kushki';
import SuccessRedirect from '../../success-redirect/success-redirect';
import { getAdyenButtonKey, getPaymentMethodExtraData } from './adyen-button.helper';
import { getUserOS, getUserSID } from '../../../../../services/quality-events/quality-events.helpers';
import usePaymentOptions from '../../../../../services/payment/use-payment-options';
// eslint-disable-next-line import/imports-first
import '@adyen/adyen-web/dist/adyen.css';
import './adyen-button.scss';

export type AdyenPaymentButtonProps = {
    adyenConfigs?: AdyeConfig[];
};

type AdyenBtnProps = {
    paymentType: string;
    paymentMethodConfiguration?: unknown;
    className?: string;
};

const AdyenBtn: FC<AdyenBtnProps> = ({
    paymentType,
    paymentMethodConfiguration = {},
    className = ''
}) => {
    const user = useAppSelector((state: RootState) => state.main.user);
    const currency = useAppSelector((state: RootState) => state.main.currency);
    const { data: paymentOptionsData } = usePaymentOptions(currency.code);
    const { paymentCart } = useAppSelector((state: RootState) => state.payment);
    const dispatch = useDispatch();
    const [redirectToSuccessPage, setRedirectToSuccessPage] = useState(false);
    const orderToken = useRef('');
    const paymentKey = getAdyenButtonKey(paymentType);
    const supportedCurrency = paymentOptionsData?.currencies[paymentKey] ?? paymentOptionsData?.currencies?.[PAYMENT_METHOD_KEYS.APPLE_PAY];

    useEffect(() => {
        initAdyenButton();
    }, []);

    const initAdyenButton = async () => {
        if (!paymentKey) {
            dispatch(addPaymentError({}));

            return;
        }

        const sessionData = await getAdyenSessionData(paymentKey);

        if (!sessionData?.session) {
            dispatch(addPaymentError({}));

            return;
        }

        const adyenPaymentsWithAdditionalDetails = [PAYMENT_METHOD_KEYS.KLARNA_PAY];

        const adyenCheckout = await createAdyenCheckout(
            sessionData,
            onAdyenButtonSubmit,
            onPaymentCompleted,
            adyenPaymentsWithAdditionalDetails.includes(paymentKey) ? onAdditionalDetails : undefined,
            (error) => {
                console.warn({ error });

                // Close Payment UI modal
                // @ts-ignore
                if (error?.name === 'CANCEL' || error?.approved === false) {
                    qualityEvents.checkoutPage.abandonedPayment(user.id, getAdyenButtonKey(paymentType), paymentCart?.finalPrice);
                } else {
                    dispatch(addPaymentError({}));
                    qualityEvents.checkoutPage.failedPayment(user.id, paymentKey, paymentCart?.finalPrice, error?.message);
                }
            }
        );

        try {
            const adyenComponent = adyenCheckout?.create(paymentType, {
                showPayButton: true,
                // @ts-ignore
                ...paymentMethodConfiguration
            });

            adyenComponent.mount('#AdyenButton');
        } catch (err) {
            dispatch(addFailedPaymentMethod(paymentKey, (err as Error)?.message));
            console.info(`Something went wrong during initialization ${paymentKey}`, err);
        }
    };

    const onAdyenButtonSubmit = (state: unknown, component: UIElement) => {
        const params = serializeQueryParams();
        const formData = dispatch(serializeForm());
        const isInvoiceFormValid = dispatch(validateForm());
        const requestConfig: { [key: string]: string | boolean } = {};

        if (!isInvoiceFormValid) {
            component.setStatus('ready');

            return;
        }

        qualityEvents.checkoutPage.startPayment(user.id, paymentKey, paymentCart?.finalPrice);

        dispatch(changeState({ isDataProcessing: true }));

        if (params.materialId) {
            requestConfig.materialId = params.materialId as string;
        }

        // @ts-ignore
        emailHash.storeSHA256(formData.email);

        orderToken.current = '';
        const mobileParams = getMobilePathParams(true) as Object;

        const extraData = getPaymentMethodExtraData(paymentType, state);

        axios
            .post(
                `${API_URLS.PAYMENT_ATTEMPT}/${paymentKey}`,
                {
                    // @ts-ignore
                    browserInfo: state?.data?.browserInfo,
                    acceptAgb: 'on',
                    currencyCode: supportedCurrency,
                    deepLinkRedirect: isMobileAppWithDeeplinkRedirect(),
                    invoiceRequested: false,
                    sid: getUserSID(),
                    ...(getUserOS() && { os: getUserOS() }),
                    ...extraData,
                    ...formData,
                    ...mobileParams
                },
                { params: { ...requestConfig } }
            )
            .then((response) => {
                orderToken.current = response.orderToken;

                if (component && response.action) {
                    component.handleAction(response.action);

                    return;
                }

                onPaymentCompleted(response);
            })
            .catch((err) => {
                dispatch(addPaymentError({}));
                qualityEvents.checkoutPage.failedPayment(user.id, paymentKey, paymentCart?.finalPrice, err?.data?.message || err?.data?.reason);
                component.setStatus('error');
            })
            .finally(() => dispatch(changeState({ isDataProcessing: false })));
    };

    const onPaymentCompleted = (result: OnPaymentCompletedData) => {
        if (!orderToken.current || result.resultCode !== 'Authorised') {
            dispatch(addPaymentError({}));
            qualityEvents.checkoutPage.failedPayment(user.id, paymentKey, paymentCart?.finalPrice, result?.resultCode);

            return;
        }

        setRedirectToSuccessPage(true);
    };

    const onAdditionalDetails = (state: never, component?: UIElement) => {
        // @ts-ignore
        if (state?.data?.details?.token) {
            axios
                .post(`${API_URLS.PAYMENT_ATTEMPT_CALLBACK_ADYEN}`, {
                    paymentMethod: paymentKey,
                    // @ts-ignore
                    paymentData: state.data?.paymentData,
                    // @ts-ignore
                    authorization_token: state.data?.details?.authorization_token
                })
                .then((response) => {
                    setRedirectToSuccessPage(true);
                })
                .catch((err) => {
                    dispatch(addPaymentError({}));
                    qualityEvents.checkoutPage.failedPayment(user.id, paymentKey, paymentCart?.finalPrice, err?.data?.message || err?.data?.reason);
                });
        }
    };

    return paymentKey ? (
        <div className={`adyen-btn-wrapper ${className}`}>
            <div id='AdyenButton' />
            <SuccessRedirect needRedirect={redirectToSuccessPage} orderToken={orderToken.current} />
        </div>
    ) : (
        <></>
    );
};

export default AdyenBtn;
